import React, { useState } from 'react';
import { Container, Table, Button, Form, Row, Col, Card, Modal } from 'react-bootstrap';
import { Checkbox, Select } from 'antd';
import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import DateRangePicker from '@/common/DateRangePicker/DateRangePicker';
import FormItem from '@/common/FormItem/FormItem';
import dayjs from 'dayjs';
import TitleHelmet from '@/components/TitleHelmet';
import PageTitle from '@/common/PageTitle/PageTitle';
import BrandButton from '@/common/BrandButton/BrandButton';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import styles from './talent-requests.module.scss';


interface TalentRequest {
  id: string;
  message: string;
  status: 'pending_approval' | 'live' | 'disabled' | 'approved' | 'rejected';
  revision: number;
  client: string;
  brand: string;
  createdAt: string;
  updatedAt: string;
  talent?: string;
  feedback?: string;
}

const mockTalentRequests: TalentRequest[] = [
  {
    id: 'fb808291-e97e-4ba5-9204-66f6b566b8b3',
    message: 'Thanks For Listening to Classic Chevrolet Radio',
    status: 'disabled',
    revision: 1,
    client: 'Classic Chevrolet Inc',
    brand: 'Classic Chevrolet Inc',
    createdAt: '2024-01-15',
    updatedAt: '2024-01-20'
  },
  {
    id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    message: 'Very Happy Holiday',
    status: 'pending_approval',
    revision: 1,
    client: 'Spinoso Management Group',
    brand: 'Spinoso Management Group',
    createdAt: '2024-01-16',
    updatedAt: '2024-01-21'
  },
  {
    id: 'b2c3d4e5-f6g7-8901-bcde-f23456789012',
    message: 'Messaging',
    status: 'live',
    revision: 1,
    client: 'KrossKast',
    brand: 'KrossKast',
    createdAt: '2024-01-14',
    updatedAt: '2024-01-19'
  },
  {
    id: 'c3d4e5f6-g7h8-9012-cdef-345678901234',
    message: 'KrossKast Branding (J2)',
    status: 'approved',
    revision: 1,
    client: 'PhiLLKross',
    brand: 'KrossKast',
    createdAt: '2024-01-13',
    updatedAt: '2024-01-18'
  },
  {
    id: 'd4e5f6g7-h8i9-0123-defg-456789012345',
    message: 'Happy Holidays',
    status: 'live',
    revision: 1,
    client: 'Cheeks Bean Gelato Co',
    brand: 'Cheeks & Bean Gelato Co.',
    createdAt: '2024-01-12',
    updatedAt: '2024-01-17'
  },
  {
    id: 'e5f6g7h8-i9j0-1234-efgh-567890123456',
    message: "You're Listening To The Kross Cafe",
    status: 'pending_approval',
    revision: 1,
    client: 'KrossKast Demo',
    brand: 'KrossKast Demo',
    createdAt: '2024-01-11',
    updatedAt: '2024-01-16'
  },
  {
    id: 'f6g7h8i9-j0k1-2345-fghi-678901234567',
    message: 'KK Messaging (Reese\'s)',
    status: 'live',
    revision: 1,
    client: 'Canteen',
    brand: 'Canteen',
    createdAt: '2024-01-10',
    updatedAt: '2024-01-15'
  },
  {
    id: 'g7h8i9j0-k1l2-3456-ghij-789012345678',
    message: 'Welcome to Texadelphia',
    status: 'disabled',
    revision: 1,
    client: 'Texadelphia',
    brand: 'Texadelphia',
    createdAt: '2024-01-09',
    updatedAt: '2024-01-14'
  }
];

const TalentRequests: React.FC = () => {
  const [talentRequests, setTalentRequests] = useState<TalentRequest[]>(mockTalentRequests);
  const [selectedRequest, setSelectedRequest] = useState<TalentRequest | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);
  const [dateRange, setDateRange] = useState<any>(null);
  const [checkedList, setCheckedList] = useState<string[]>([]);
  const [checkAll, setCheckAll] = useState(false);
  const [filters, setFilters] = useState({
    brand: null as string | null,
    status: null as string | null,
    client: null as string | null,
    message: null as string | null
  });
  const [resetCounter, setResetCounter] = useState(0);

  // Filter options
  const filterOptions = {
    brand: Array.from(new Set(talentRequests.map(request => request.brand))).map(brand => ({ value: brand, label: brand })),
    status: [
      { value: 'pending_approval', label: 'Pending Approval' },
      { value: 'live', label: 'Live' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'disabled', label: 'Disabled' }
    ],
    client: Array.from(new Set(talentRequests.map(request => request.client))).map(client => ({ value: client, label: client })),
    message: talentRequests.map(request => ({ value: request.message, label: request.message }))
  };

  const handleEdit = (request: TalentRequest) => {
    setSelectedRequest(request);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedRequest(null);
  };

  const handleNewRequest = () => {
    setShowNewRequestModal(true);
  };

  const handleCloseNewRequestModal = () => {
    setShowNewRequestModal(false);
  };

  const handleDelete = (requestId: string) => {
    if (window.confirm('Are you sure you want to delete this talent request?')) {
      setTalentRequests(prev => prev.filter(r => r.id !== requestId));
    }
  };

  const handleDateChange = (dates: any) => {
    setDateRange(dates);
  };

  const handleFilterChange = (filterName: string, value: string | null) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      brand: null,
      status: null,
      client: null,
      message: null
    });
    setDateRange(null);
    setResetCounter(prev => prev + 1);
  };

  const handleOnCheckAllChange = (e: any) => {
    setCheckAll(e.target.checked);
    if (e.target.checked) {
      setCheckedList(filteredRequests.map(request => request.id));
    } else {
      setCheckedList([]);
    }
  };

  const handleOnCheckboxChange = (e: any, request: TalentRequest) => {
    const { checked } = e.target;
    if (checked) {
      setCheckedList(prev => [...prev, request.id]);
    } else {
      setCheckedList(prev => prev.filter(id => id !== request.id));
    }
  };

  const filteredRequests = talentRequests.filter(request => {
    if (filters.brand && !request.brand.toLowerCase().includes(filters.brand.toLowerCase())) return false;
    if (filters.status && request.status !== filters.status) return false;
    if (filters.client && !request.client.toLowerCase().includes(filters.client.toLowerCase())) return false;
    if (filters.message && !request.message.toLowerCase().includes(filters.message.toLowerCase())) return false;
    return true;
  });

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'live':
        return styles.statusLive;
      case 'pending_approval':
        return styles.statusPending;
      case 'approved':
        return styles.statusApproved;
      case 'rejected':
        return styles.statusRejected;
      case 'disabled':
        return styles.statusDisabled;
      default:
        return styles.statusDefault;
    }
  };

  return (
    <React.Fragment>
      <TitleHelmet title="Talent Requests" />
      <Container fluid>
        <Card>
          <Card.Body>
            <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
              <PageTitle title="Talent Requests" />
              <Button 
                onClick={handleResetFilters}
                style={{ 
                  background: 'white', 
                  color: '#333', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontWeight: '500'
                }}
              >
                <FilterOutlined /> Reset All
              </Button>
            </Card.Title>

            {/* Filters Section */}
            <Form className={styles.form}>
              <Row>
                <Col>
                  <Row>
                    <Col md={3}>
                      <FormItem
                        label="Client"
                        className="customSelectWrapper"
                        layout="vertical"
                      >
                        <Select
                          key={`client-reset-${resetCounter}`}
                          showSearch
                          allowClear
                          className={`customSelect ${styles.selectField}`}
                          placeholder="Any client"
                          value={filters.client}
                          onChange={value => handleFilterChange('client', value)}
                          options={filterOptions.client}
                        />
                      </FormItem>
                    </Col>
                    <Col md={3}>
                      <FormItem
                        label="Brand"
                        className="customSelectWrapper"
                        layout="vertical"
                      >
                        <Select
                          key={`brand-reset-${resetCounter}`}
                          showSearch
                          allowClear
                          className={`customSelect ${styles.selectField}`}
                          placeholder="Any brand"
                          value={filters.brand}
                          onChange={value => handleFilterChange('brand', value)}
                          options={filterOptions.brand}
                        />
                      </FormItem>
                    </Col>
                    <Col md={3}>
                      <FormItem
                        label="Message"
                        className="customSelectWrapper"
                        layout="vertical"
                      >
                        <Select
                          key={`message-reset-${resetCounter}`}
                          showSearch
                          allowClear
                          className={`customSelect ${styles.selectField}`}
                          placeholder="Any message"
                          value={filters.message}
                          onChange={value => handleFilterChange('message', value)}
                          options={filterOptions.message}
                        />
                      </FormItem>
                    </Col>
                    <Col md={3}>
                      <FormItem
                        label="Status"
                        className="customSelectWrapper"
                        layout="vertical"
                      >
                        <Select
                          key={`status-reset-${resetCounter}`}
                          showSearch
                          allowClear
                          className={`customSelect ${styles.selectField}`}
                          placeholder="Any status"
                          value={filters.status}
                          onChange={value => handleFilterChange('status', value)}
                          options={filterOptions.status}
                        />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Form>
            
            <Row className="mb-2">
              <Col md={8}>
                <FormItem
                  label="Filter by Date Range"
                  layout="vertical"
                >
                  <DateRangePicker
                    key={`dateRange-reset-${resetCounter}`}
                    onChange={(dates) => {
                      if (dates && dates.length === 2) {
                        setDateRange([
                          dates[0] ? dates[0].toISOString() : '', 
                          dates[1] ? dates[1].toISOString() : ''
                        ]);
                      } else {
                        setDateRange(null);
                      }
                    }}
                    style={{ width: '100%' }}
                  />
                </FormItem>
              </Col>
              <Col md={4} className="d-flex align-items-center mt-2 mt-md-0">
                <Button 
                  className="w-100"
                  style={{ 
                    background: 'white', 
                    color: '#e21e26', 
                    border: '1px solid #e21e26', 
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    justifyContent: 'center',
                    height: '50px',
                    marginTop: '8px'
                  }}
                  onClick={handleNewRequest}
                >
                  <PlusOutlined style={{ fontSize: 16 }} />
                  Add New Talent Request
                </Button>
              </Col>
            </Row>
            
            {/* Talent Requests Table */}
            <Row>
              <Col>
                <Table hover responsive className={styles.talentRequestsTable}>
                  <thead>
                    <tr>
                      <th>
                        <Checkbox
                          checked={checkAll}
                          style={{ color: "#ffffff" }}
                          onChange={handleOnCheckAllChange}
                        />
                      </th>
                      <th style={{ color: "#ffffff" }}>Message</th>
                      <th style={{ color: "#ffffff" }}>Status</th>
                      <th style={{ color: "#ffffff" }}>Revision</th>
                      <th style={{ color: "#ffffff" }}>Client</th>
                      <th style={{ color: "#ffffff" }}>Brand</th>
                      <th style={{ color: "#ffffff", paddingLeft: '38px !important' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRequests.map((request) => (
                      <tr key={request.id}>
                        <td style={{ width: 40, textAlign: 'center', padding: '0 8px 0 20px', overflow: 'hidden' }}>
                          <div style={{ width: 24, height: 24, overflow: 'hidden', margin: '0 auto' }}>
                            <Checkbox 
                              checked={checkedList.some(id => id === request.id)}
                              onChange={(e: any) => handleOnCheckboxChange(e, request)} 
                              style={{ margin: 0, padding: 0, width: 24, height: 24 }} 
                            />
                          </div>
                        </td>
                        <td>{request.message}</td>
                        <td style={{ textAlign: 'left' }}>
                          <div style={{ textAlign: 'left', display: 'inline-block' }}>
                            <span className={`${styles.statusBadge} ${getStatusBadgeClass(request.status)}`}>
                              {request.status.replace('_', ' ')}
                            </span>
                          </div>
                        </td>
                        <td>{request.revision}</td>
                        <td>{request.client}</td>
                        <td>{request.brand}</td>
                        <td style={{ padding: '12px 15px !important' }}>
                          <div className={styles.actionButtons}>
                            <Button 
                              variant="link" 
                              size="sm" 
                              className={styles.actionLink}
                            >
                              View
                            </Button>
                            <Button 
                              variant="link" 
                              size="sm" 
                              className={styles.actionLink}
                              onClick={() => handleEdit(request)}
                            >
                              Edit
                            </Button>
                            <Button 
                              variant="link" 
                              size="sm" 
                              className={`${styles.actionLink} ${styles.deleteLink}`}
                              onClick={() => handleDelete(request.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>
            </Row>

            {/* Footer */}
            <Row className="mt-3">
              <Col md={12} className="text-end">
                <span className={styles.displayInfo}>
                  Displaying all {filteredRequests.length} Talent Requests
                </span>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>

      {/* Edit Talent Request Modal */}
      <Modal 
        show={showEditModal} 
        onHide={handleCloseEditModal}
        size="lg"
        className={styles.editModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Talent Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <Form id="edit-talent-request-form">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Message</strong></Form.Label>
                    <Form.Control
                      type="text"
                      name="message"
                      defaultValue={selectedRequest.message}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Status</strong></Form.Label>
                    <Form.Select
                      name="status"
                      defaultValue={selectedRequest.status}
                    >
                      <option value="pending_approval">Pending Approval</option>
                      <option value="live">Live</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="disabled">Disabled</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Client</strong></Form.Label>
                    <Form.Control
                      type="text"
                      name="client"
                      defaultValue={selectedRequest.client}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Brand</strong></Form.Label>
                    <Form.Control
                      type="text"
                      name="brand"
                      defaultValue={selectedRequest.brand}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label><strong>Feedback</strong></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="feedback"
                  defaultValue={selectedRequest.feedback || ''}
                  placeholder="Enter feedback for this talent request"
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditModal}>
            Cancel
          </Button>
          <Button variant="primary">
            Update Request
          </Button>
        </Modal.Footer>
      </Modal>

      {/* New Talent Request Modal */}
      <Modal 
        show={showNewRequestModal} 
        onHide={handleCloseNewRequestModal}
        size="lg"
        className={styles.editModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>New Talent Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Message</strong></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter the message to be recorded"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Client</strong></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter client name"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Brand</strong></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter brand name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label><strong>Status</strong></Form.Label>
                  <Form.Select defaultValue="pending_approval">
                    <option value="pending_approval">Pending Approval</option>
                    <option value="live">Live</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="disabled">Disabled</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label><strong>Additional Notes</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter any additional notes or requirements"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseNewRequestModal}>
            Cancel
          </Button>
          <Button variant="primary">
            Create Request
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default withAuthenticationRequired(TalentRequests); 